import React, { useMemo, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import CustomerPicker from '../../components/shared/CustomerPicker';
import CurrencySelect from '../../components/shared/CurrencySelect';
import CountrySelect from '../../components/shared/CountrySelect';
import { formatCurrency } from '../../utils/currency';
import InvoiceItemsEditor, { computeTotals } from './InvoiceItemsEditor';
import { getCountries, getCurrencies } from '../../api/reference';

export default function InvoiceFormInner({
  form,
  update,
  errors,
  products,
  openCustomerPage,
  onNewCustomer,
  isEditing = false,
}) {
  const totals = React.useMemo(() => computeTotals(form.items, form.isGst), [form.items, form.isGst]);

  const { data: countries = [] } = useQuery({
    queryKey: ['reference', 'countries'],
    queryFn: getCountries,
    staleTime: 24 * 60 * 60 * 1000, // 24h — static data
  });

  const { data: currencies = [] } = useQuery({
    queryKey: ['reference', 'currencies'],
    queryFn: getCurrencies,
    staleTime: 24 * 60 * 60 * 1000,
  });

  const orderedCurrencies = useMemo(() => {
    const order = { USD: 1, GBP: 2, EUR: 3, INR: 4 };
    return [...currencies].sort((a, b) => {
      const oA = order[a.code] || 99;
      const oB = order[b.code] || 99;
      if (oA !== oB) return oA - oB;
      return a.code.localeCompare(b.code);
    });
  }, [currencies]);

  useEffect(() => {
    if (form.billingCountry !== 'IN' && form.isGst) {
      update({ isGst: false });
    }
  }, [form.billingCountry, form.isGst, update]);

  // Track whether the user has manually overridden the currency.
  // Start as `true` when editing an existing invoice so the saved currency
  // is not overwritten when the form first populates billingCountry.
  const userOverrideCurrency = useRef(isEditing);

  // Country code → default currency code map (hardcoded fallback + API data)
  const countryCurrencyMap = useMemo(() => {
    const map = {};
    countries.forEach((c) => {
      if (c.currencyCode) map[c.code] = c.currencyCode;
    });
    // Fallback hardcoded map for common countries
    const fallback = {
      IN: 'INR', US: 'USD', GB: 'GBP', EU: 'EUR', AU: 'AUD', CA: 'CAD',
      JP: 'JPY', CN: 'CNY', AE: 'AED', SG: 'SGD', NZ: 'NZD', CH: 'CHF',
      ZA: 'ZAR', MX: 'MXN', BR: 'BRL', DE: 'EUR', FR: 'EUR', IT: 'EUR',
      ES: 'EUR', NL: 'EUR', SE: 'SEK', NO: 'NOK', DK: 'DKK', HK: 'HKD',
      KR: 'KRW', MY: 'MYR', TH: 'THB', ID: 'IDR', PH: 'PHP', PK: 'PKR',
      BD: 'BDT', LK: 'LKR', NP: 'NPR', NG: 'NGN', KE: 'KES', EG: 'EGP',
      AR: 'ARS', CL: 'CLP', CO: 'COP', TR: 'TRY', RU: 'RUB', IL: 'ILS',
      SA: 'SAR', QA: 'QAR', KW: 'KWD', BH: 'BHD', OM: 'OMR', JO: 'JOD',
    };
    return { ...fallback, ...map };
  }, [countries]);

  // Helper: given a country code, return the mapped currency (or undefined)
  const getCurrencyForCountry = (countryCode) => countryCurrencyMap[countryCode];

  // When billingCountry changes (user manually selects country dropdown),
  // auto-set currency unless the user has already manually overridden it.
  useEffect(() => {
    if (userOverrideCurrency.current) return;
    const defaultCurrency = countryCurrencyMap[form.billingCountry];
    if (defaultCurrency && defaultCurrency !== form.currency) {
      update({ currency: defaultCurrency });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.billingCountry, countryCurrencyMap]);



  const inputClass = 'w-full h-[34px] border border-slate-300 rounded-md bg-white text-slate-900 text-[13px] px-[10px] focus:outline-none focus:border-blue-500';
  const labelClass = 'block text-[11px] font-medium text-slate-500 uppercase tracking-[0.04em] mb-[5px]';
  const optionalTag = <span className="text-[11px] font-normal text-slate-400 normal-case tracking-normal">(optional)</span>;

  const handleNewCustomerClick = () => {
    if (onNewCustomer) {
      onNewCustomer();
    } else if (openCustomerPage) {
      openCustomerPage();
    }
  };

  return (
    <div className="flex flex-col gap-[14px]">
      {/* Customer */}
      <div className="mb-1">
        <label className={labelClass}>Customer <span className="text-[#D85A30]">*</span></label>
        <div className="flex items-center gap-[6px]">
          <div className="flex-1">
            <CustomerPicker
              value={form.customerId}
              onChange={(id) => update({ customerId: id })}
              onCustomerSelect={(customer) => {
                if (!customer) return;
                // Auto-fill billing country from customer if available
                if (customer.billingCountry) {
                  const customerCurrency =
                    customer.currency ||
                    getCurrencyForCountry(customer.billingCountry);
                  userOverrideCurrency.current = false;
                  update({
                    customerId: customer.id,
                    billingCountry: customer.billingCountry,
                    // Apply customer's saved currency, or auto-map from country.
                    // We do this explicitly here because the useEffect won't
                    // re-fire if billingCountry value didn't change.
                    ...(customerCurrency ? { currency: customerCurrency } : {}),
                  });
                  if (customer.currency) {
                    // Customer has an explicit saved currency — lock it in
                    userOverrideCurrency.current = true;
                  }
                } else if (customer.currency) {
                  userOverrideCurrency.current = true;
                  update({ currency: customer.currency });
                }
              }}
              error={!!errors.customerId}
            />
          </div>
          {(onNewCustomer || openCustomerPage) && (
            <button
              type="button"
              onClick={handleNewCustomerClick}
              className="h-[34px] px-[12px] border border-slate-300 rounded-md bg-transparent text-slate-500 text-[13px] cursor-pointer whitespace-nowrap flex-shrink-0 font-sans hover:bg-slate-100 hover:text-slate-900 transition-colors"
            >
              + New
            </button>
          )}
        </div>
      </div>

      {/* Dates */}
      <div className="grid grid-cols-2 gap-[10px]">
        <div>
          <label className={labelClass}>Invoice date <span className="text-[#D85A30]">*</span></label>
          <input
            type="date"
            value={form.invoiceDate || ''}
            onChange={(e) => update({ invoiceDate: e.target.value })}
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Due date <span className="text-[#D85A30]">*</span></label>
          <div className="flex gap-[6px]">
            <select
              className={`${inputClass} flex-1`}
              value={
                [30, 45, 60, 75, 90].find(d => {
                  const presetDate = new Date(form.invoiceDate || new Date());
                  presetDate.setDate(presetDate.getDate() + d);
                  return presetDate.toISOString().split('T')[0] === form.dueDate;
                }) || 'custom'
              }
              onChange={(e) => {
                const val = e.target.value;
                if (val === 'custom') {
                  // don't change date, let user use the date picker
                } else {
                  const d = new Date(form.invoiceDate || new Date());
                  d.setDate(d.getDate() + Number(val));
                  update({ dueDate: d.toISOString().split('T')[0] });
                }
              }}
            >
              <option value="30">30 Days</option>
              <option value="45">45 Days</option>
              <option value="60">60 Days</option>
              <option value="75">75 Days</option>
              <option value="90">90 Days</option>
              <option value="custom">Custom</option>
            </select>
            <input
              type="date"
              value={form.dueDate || ''}
              onChange={(e) => update({ dueDate: e.target.value })}
              className={`w-[130px] h-[34px] border ${errors.dueDate ? 'border-red-400' : 'border-slate-300'} rounded-md bg-white text-slate-900 text-[13px] px-[10px] focus:outline-none focus:border-blue-500`}
            />
          </div>
        </div>
      </div>

      {/* PO & Contact */}
      <div className="grid grid-cols-2 gap-[10px]">
        <div>
          <label className={labelClass}>PO Number {optionalTag}</label>
          <input
            type="text"
            value={form.poNumber || ''}
            onChange={(e) => update({ poNumber: e.target.value })}
            placeholder="e.g. PO-12345"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Contact Person {optionalTag}</label>
          <input
            type="text"
            value={form.contactPerson || ''}
            onChange={(e) => update({ contactPerson: e.target.value })}
            placeholder="Client contact name"
            className={inputClass}
          />
        </div>
      </div>

      {/* Product & Invoice Type */}
      <div className="grid grid-cols-2 gap-[10px]">
        <div>
          <label className={labelClass}>Product <span className="text-[#D85A30]">*</span></label>
          <select
            value={form.productId || ''}
            onChange={(e) => update({ productId: e.target.value })}
            className={`${inputClass} ${errors.productId ? 'border-red-400' : ''}`}
          >
            <option value="">Select a product...</option>
            {products?.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>Invoice type {optionalTag}</label>
          <input
            type="text"
            value={form.invoiceType || ''}
            onChange={(e) => update({ invoiceType: e.target.value })}
            placeholder="e.g. SERVICE"
            className={inputClass}
          />
        </div>
      </div>

      {/* Country & Currency */}
      <div className="grid grid-cols-2 gap-[10px]">
        <div>
          <label className={labelClass}>Billing Country {optionalTag}</label>
          <CountrySelect
            countries={countries}
            value={form.billingCountry || 'IN'}
            onChange={(code) => {
              userOverrideCurrency.current = false;
              update({ billingCountry: code });
            }}
          />
        </div>
        <div>
          <label className={labelClass}>Currency <span className="text-[#D85A30]">*</span></label>
          <CurrencySelect
            currencies={orderedCurrencies}
            value={form.currency || 'INR'}
            onChange={(code) => {
              userOverrideCurrency.current = true;
              update({ currency: code });
            }}
            error={!!errors.currency}
          />
        </div>
      </div>

      {/* GST toggle */}
      {form.billingCountry === 'IN' && (
        <div className="flex items-center justify-between p-[10px_12px] bg-slate-50 rounded-md border border-slate-200">
          <div>
            <p className="text-[13px] font-medium text-slate-900 m-0">GST invoice</p>
          </div>
          <label className="relative w-[34px] h-[18px] flex-shrink-0 cursor-pointer">
            <input
              type="checkbox"
              checked={form.isGst}
              onChange={(e) => update({ isGst: e.target.checked })}
              className="sr-only peer"
            />
            <div className="absolute inset-0 rounded-[9px] bg-slate-300 transition-colors peer-checked:bg-[#1D9E75]"></div>
            <div className="absolute w-[12px] h-[12px] left-[3px] top-[3px] rounded-full bg-white transition-transform peer-checked:translate-x-[16px]"></div>
          </label>
        </div>
      )}

      {/* Created By override */}
      <div>
        <label className={labelClass}>Created By {optionalTag}</label>
        <input
          type="text"
          value={form.createdByName || ''}
          onChange={(e) => update({ createdByName: e.target.value })}
          placeholder="Override your name on the invoice"
          className={inputClass}
        />
      </div>

      {/* Notes */}
      <div>
        <label className={labelClass}>Notes {optionalTag}</label>
        <textarea
          value={form.notes || ''}
          onChange={(e) => update({ notes: e.target.value })}
          placeholder="Payment terms, references, or anything the customer should see…"
          className="w-full h-[64px] resize-none p-[8px_10px] leading-[1.5] border border-slate-300 rounded-md bg-white text-slate-900 text-[13px] focus:outline-none focus:border-blue-500"
        ></textarea>
      </div>

      {/* Items */}
      <div>
        <InvoiceItemsEditor
          items={form.items}
          onChange={(items) => update({ items })}
          errors={errors.itemRows || {}}
          isGst={form.isGst}
          currency={form.currency || 'INR'}
        />
      </div>

      {/* Hidden subtotal (used by parent footer) */}
      <div className="pt-2 border-t border-slate-200 mt-2 flex justify-between items-center hidden">
        <span className="text-[12px] text-slate-500">Subtotal</span>
        <span className="text-[15px] font-medium text-slate-900">{formatCurrency(totals.total, form.currency || 'INR')}</span>
      </div>
    </div>
  );
}

